import { z } from 'zod';
import { zGoDate, zGoNullableDate, zGoSlice } from '../helpers/fix-go-types';
import { casesOf, OrderItemSchema, totalCost, unitsOf } from './order-item';
import { OrderOptionsSchema } from './order-options';
import { BounceRecordSchema } from './representative';

export enum OrderStatus {
  Draft = 'draft',
  Sending = 'sending',
  Sent = 'sent',
  Canceled = 'canceled',
  Failed = 'failed',
}

const DownloadInfoSchema = z.object({
  file_count: z.number(),
  max_rows: z.number().default(0),
  bulk_add_link: z.string().default(''),
  bulk_add_label: z.string().default(''),
});

// DeliveryFailure mirrors the Go models.DeliveryFailure (an Email plus an
// embedded BounceRecord, flat on the wire).
export const DeliveryFailureSchema = BounceRecordSchema.extend({
  email: z.string().default(''),
});

export type DeliveryFailure = z.infer<typeof DeliveryFailureSchema>;

// form validation rules
export const OrderSchema = z.object({
  store_id: z.string(),
  id: z.string(),
  vendor_id: z.string(),

  options: OrderOptionsSchema,
  items: zGoSlice(OrderItemSchema),
  status: z.enum(OrderStatus),
  send_method: z.string().optional(),
  download_info: DownloadInfoSchema.nullish().default(null),

  created_at: zGoDate,
  updated_at: zGoDate,
  submitted_at: zGoNullableDate,

  ses_message_id: z.string().default(''),
  sent_rep_emails: zGoSlice(z.string()),
  delivery_failures: zGoSlice(DeliveryFailureSchema),

  // Server-owned optimistic-concurrency counter (Plan 3). Absent on any order
  // cached before v2 shipped, so it must default rather than be required —
  // persisted-cache rehydration bypasses schema defaults until the next
  // refetch, and 0 is safely "older than anything the server will push".
  version: z.number().default(0),

  // Serialization-time aggregates. Computed from the UNSCRUBBED order server
  // side, so they are correct even for a v1 store whose suggestions are
  // zeroed on the wire.
  total_dollars: z.number().default(0),
  total_cases: z.number().default(0),
  total_units: z.number().default(0),
});

export type Order = z.infer<typeof OrderSchema>;

// form validation rules
const CreateOrderSchema = z.object({
  vendor_id: z.string(),
  options: OrderOptionsSchema.optional(),
  items: z.array(OrderItemSchema).optional(),
});

export type CreateOrder = z.infer<typeof CreateOrderSchema>;

// Helper functions for calculating order totals
export const caseCount = (items: Order['items']) => {
  return items.reduce((sum, item) => sum + casesOf(item), 0);
};

export const unitCount = (items: Order['items']) => {
  return items.reduce((sum, item) => sum + unitsOf(item), 0);
};

export const orderTotalCost = (items: Order['items']) => {
  return items.reduce((sum, item) => sum + totalCost(item), 0);
};

export type OrderDeliveryState = 'clean' | 'partial' | 'failed' | 'unknown';

// bouncedEmails is the lowercased set of addresses that bounced for an
// order. Casing is normalized here because SES reports recipients in the
// (lowercased) form we sent them, while sent_rep_emails are stored as-sent.
//
// delivery_failures is optional on the wire (omitempty) — the OrderSchema
// default only fills it in for fetched orders, but these helpers also run on
// order objects built client-side (optimistic cache updates), so guard the
// absent case rather than assume the field is always present.
function bouncedEmails(order: Order): Set<string> {
  return new Set((order.delivery_failures ?? []).map((f) => f.email.toLowerCase()));
}

// orderDeliveryState classifies an order's email delivery from the
// vendor's perspective:
//   failed  — every rep address we emailed bounced; the vendor almost
//             certainly never received the order.
//   partial — at least one rep bounced, but at least one other rep
//             address was reachable.
//   unknown — the order recorded delivery failures but we have no record
//             of which reps it was emailed to (an email order identified by
//             ses_message_id, sent before sent_rep_emails shipped). Surface
//             a generic warning rather than hide a real problem.
//   clean   — nothing relevant bounced. A bounce on a store cc address
//             (not in sent_rep_emails) does not downgrade the state: the
//             vendor still received the order.
export function orderDeliveryState(order: Order): OrderDeliveryState {
  if ((order.delivery_failures ?? []).length === 0) {
    return 'clean';
  }

  const repEmails = (order.sent_rep_emails ?? []).map((e) => e.toLowerCase());
  if (repEmails.length === 0) {
    // No recorded rep recipients — an email order sent before
    // sent_rep_emails shipped. A non-empty ses_message_id means we
    // emailed the vendor via SES (set only on email sends, never on
    // ftp/download), so warn generically; otherwise delivery doesn't
    // depend on the vendor email, so stay clean.
    return order.ses_message_id ? 'unknown' : 'clean';
  }

  const bounced = bouncedEmails(order);
  const bouncedReps = repEmails.filter((e) => bounced.has(e));

  if (bouncedReps.length === 0) {
    return 'clean';
  }

  return bouncedReps.length >= repEmails.length ? 'failed' : 'partial';
}

// orderDeliveredReps returns the vendor representative addresses that DID
// receive the order: the ones we emailed (sent_rep_emails) minus the ones
// that bounced. Casing is preserved for display; the bounce match is
// case-insensitive. Empty for failed orders (every rep bounced) and for
// legacy orders with no recorded reps (we can't say who received it).
export function orderDeliveredReps(order: Order): string[] {
  const bounced = bouncedEmails(order);

  return (order.sent_rep_emails ?? []).filter((e) => !bounced.has(e.toLowerCase()));
}
